import React from 'react';
import { Box, Checkbox, FormControlLabel, Stack } from '@mui/material';

import { distanceEquals } from '@paragon/geometry-library';
import {
  EditableCeilingPlaneProperties,
  EditableRoofPlaneProperties,
  EditedCeilingPlaneProperties,
  EditedRoofPlaneProperties,
} from '@paragon/layout';
import { AngleField, DistanceField } from '@paragon/material';

import { getAbsolutePlaneElevationFromActiveLevel } from '../../../../../store/selectors/layoutDataSelectors';
import { RoofPlaneDefinitionField } from '../../../../controls/RoofPlaneDefinitionField';
import { useAppSelector } from '../../../../hooks';
import { PrimaryHeelSettingsMethodInputs } from '../HeelSettings';
import { SyncElevationButton } from '../SyncElevationButton';

interface PropsForPlaneType<PlaneType, EditableProperties, EditedProperties> {
  readonly type: PlaneType;
  readonly properties: EditableProperties;
  readonly onChange: (changes: EditedProperties) => void | Promise<void>;
  readonly disabled?: boolean;
}

export function PlanePropertyControls(
  props:
    | PropsForPlaneType<'roof', EditableRoofPlaneProperties, EditedRoofPlaneProperties>
    | PropsForPlaneType<'ceiling', EditableCeilingPlaneProperties, EditedCeilingPlaneProperties>,
) {
  const { geometry } = props.properties;

  const absolutePlaneElevationFromLevel = useAppSelector(getAbsolutePlaneElevationFromActiveLevel);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ paddingTop: 1 }}>
        <RoofPlaneDefinitionField type={props.type} value={props.properties.geometry} disabled={props.disabled} />

        <FormControlLabel
          label="Flipped"
          control={
            <Checkbox
              checked={props.properties.flipped}
              onChange={async (_, flipped) => {
                await props.onChange({ flipped });
              }}
              disabled={props.disabled}
            />
          }
        />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 1, marginTop: '8px!important' }}>
        <AngleField
          label="Slope"
          defaultDisplayFormat="slope"
          value={props.properties.slopeInDegrees}
          onSubmit={async (slopeInDegrees) => {
            await props.onChange({ slopeInDegrees });
          }}
          disabled={props.disabled}
        />

        {'overhang' in props.properties && (
          <DistanceField
            label="Overhang"
            value={props.properties.overhang}
            onSubmit={async (overhang) => {
              await props.onChange({ overhang });
            }}
            disabled={props.disabled}
          />
        )}

        {'heelSettings' in props.properties && 'topChordWidth' in props.properties && (
          <>
            <PrimaryHeelSettingsMethodInputs
              heelSettings={props.properties.heelSettings}
              topChordWidth={props.properties.topChordWidth}
              topChordSlopeInDegrees={props.properties.slopeInDegrees}
              submit={async (heelSettings) => await props.onChange({ heelSettings })}
              disabled={props.disabled}
            />
            <DistanceField
              label="Top Chord Width"
              value={props.properties.topChordWidth}
              min={0}
              max={12}
              onSubmit={async (topChordWidth) => await props.onChange({ topChordWidth })}
              disabled={props.disabled}
            />
          </>
        )}

        {'subfasciaThickness' in props.properties && (
          <DistanceField
            label="Subfascia Thickness"
            value={props.properties.subfasciaThickness}
            onSubmit={async (subfasciaThickness) => {
              await props.onChange({ subfasciaThickness });
            }}
            disabled={props.disabled}
          />
        )}

        {'cantilever' in props.properties && (
          <DistanceField
            label="Cantilever"
            value={props.properties.cantilever}
            onSubmit={async (cantilever) => await props.onChange({ cantilever })}
            disabled={props.disabled}
          />
        )}

        {'deckingThickness' in props.properties && (
          <DistanceField
            label="Decking Thickness"
            min={0}
            value={props.properties.deckingThickness}
            onSubmit={async (deckingThickness) => await props.onChange({ deckingThickness })}
            disabled={props.disabled}
          />
        )}

        {geometry?.type === 'Absolute' && (
          <>
            <DistanceField
              label="Elevation"
              value={geometry.elevation}
              onSubmit={async (elevation) => {
                await props.onChange({ geometry: { ...geometry, elevation } });
              }}
              disabled={props.disabled}
            />

            <SyncElevationButton
              sx={{ marginTop: 1, gridColumn: 'span 2' }}
              disabled={props.disabled || absolutePlaneElevationFromLevel === null}
              visible={props.properties.levelGuid != null}
              synchronized={
                absolutePlaneElevationFromLevel !== null &&
                distanceEquals(geometry.elevation, absolutePlaneElevationFromLevel)
              }
              onSynchronize={async () => {
                if (absolutePlaneElevationFromLevel === null)
                  throw new Error('sync elevation button for absolute plane clicked when it was disabled.');

                await props.onChange({ geometry: { ...geometry, elevation: absolutePlaneElevationFromLevel } });
              }}
            />
          </>
        )}
      </Box>
    </>
  );
}
